import React, { createContext, useContext, useEffect, useState } from 'react';
import { getFirestore, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { useUserData } from './UserDataFetcher';
import { app, auth } from '../firebaseConfig';

type UserDataMinimal = {
  dealerId?: string;
  manufacturerId?: string;
  role?: string;
  email?: string;
  isAdmin?: boolean;
  password?: string;
  [key: string]: any;
};

type UsersCtx = {
  users: UserDataMinimal[];
  refresh: () => Promise<void>;
  // createUser will call the admin cloud function
  createUser: (payload: {
    email: string;
    password: string;
    role?: string;
    dealerId?: string;
    manufacturerId?: string;
    isAdmin?: boolean;
    name: string;
  }) => Promise<{ uid: string }>;
  // added deleteUsers: accepts array of uids or emails (prefer uids)
  deleteUsers: (emails: string[]) => Promise<any>;
  // bulk password updates: each item must include password and either uid or email
  updateUserPasswords: (updates: Array<{ uid?: string; email?: string; password: string }>) => Promise<any>;
  // bulk user document updates: each item must include uid or email and fields object
  updateUsers: (updates: Array<{ uid?: string; email?: string; fields: Record<string, any> }>) => Promise<any>;
};

const UsersByManufacturerContext = createContext<UsersCtx | undefined>(undefined);

export const UsersDataFetcher: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [users, setUsers] = useState<UserDataMinimal[]>([]);
  const userData = useUserData();
  const db = getFirestore();

  const fetchUsers = async () => {
    if (!userData?.manufacturerId && !userData?.dealerId) {
      setUsers([]);
      return;
    }
    if (userData?.role === 'manufacturer') {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('manufacturerId', '==', userData.manufacturerId));
        const snap = await getDocs(q);
        const list: UserDataMinimal[] = [];
        snap.forEach((d) => list.push(d.data() as UserDataMinimal));
        setUsers(list);
      } catch (err) {
        console.error('UsersDataFetcher: failed to fetch users by manufacturerId', err);
        setUsers([]);
      }
    } else if (userData?.role === 'dealer') {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('dealerId', '==', userData.dealerId));
        const snap = await getDocs(q);
        const list: UserDataMinimal[] = [];
        snap.forEach((d) => list.push(d.data() as UserDataMinimal));
        setUsers(list);
      } catch (err) {
        console.error('UsersDataFetcher: failed to fetch users by dealerId', err);
        setUsers([]);
      }
    }
  };

  // call the admin createUser cloud function via HTTPS using API key from app options
  const createUser = async (payload: {
    email: string;
    password: string;
    name: string;
    role?: string;
    dealerId?: string;
    manufacturerId?: string;
    isAdmin?: boolean;
  }): Promise<{ uid: string }> => {
    const options: any = app?.options || {};
    const projectId = options.projectId as string | undefined;
    const apiKey = options.apiKey as string | undefined;
    if (!projectId || !apiKey) {
      throw new Error('client-config: missing projectId or apiKey');
    }

    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/createUser?key=${apiKey}`;

    // ensure authenticated user token is sent
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('unauthenticated: must be signed in to call createUser');
    }
    const idToken = await currentUser.getIdToken();

    const body = { data: payload };

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('createUser() remote error:', res.status, text);
        throw new Error(`remote-error: ${res.status}`);
      }

      const json = await res.json();
      // callable functions often return { result: { ... } } or just { uid: ... } depending on setup
      const result = (json && (json.result ?? json)) as any;
      if (!result || !result.uid) {
        throw new Error('invalid-response: missing uid');
      }
      // refresh local users list after creating
      await fetchUsers();
      return { uid: result.uid };
    } catch (err) {
      console.error('createUser() failed:', err);
      throw err;
    }
  };

  // call the admin deleteUsers cloud function via HTTPS using API key from app options
  const deleteUsers = async (emails: string[]): Promise<any> => {
    const options: any = app?.options || {};
    const projectId = options.projectId as string | undefined;
    const apiKey = options.apiKey as string | undefined;
    if (!projectId || !apiKey) {
      throw new Error('client-config: missing projectId or apiKey');
    }

    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/deleteUsers?key=${apiKey}`;

    // ensure authenticated user token is sent
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('unauthenticated: must be signed in to call deleteUsers');
    }
    const idToken = await currentUser.getIdToken();

    // Callable functions expect { data: { ... } }
    const body = { data: { emails } };

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('deleteUsers() remote error:', res.status, text);
        throw new Error(`remote-error: ${res.status}`);
      }

      const json = await res.json();
      const result = (json && (json.result ?? json)) as any;
      return result;
    } catch (err) {
      console.error('deleteUsers() failed:', err);
      throw err;
    }
  };

  // call the admin updateUserPasswords cloud function via HTTPS using API key from app options
  const updateUserPasswords = async (
    updates: Array<{ uid?: string; email?: string; password: string }>
  ): Promise<any> => {
    const options: any = app?.options || {};
    const projectId = options.projectId as string | undefined;
    const apiKey = options.apiKey as string | undefined;
    if (!projectId || !apiKey) {
      throw new Error('client-config: missing projectId or apiKey');
    }

    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/updateUserPasswords?key=${apiKey}`;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('unauthenticated: must be signed in to call updateUserPasswords');
    }
    const idToken = await currentUser.getIdToken();

    const body = { data: { updates } };

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('updateUserPasswords() remote error:', res.status, text);
        throw new Error(`remote-error: ${res.status}`);
      }

      const json = await res.json();
      const result = (json && (json.result ?? json)) as any;
      // refresh local users list after password changes (audit timestamp may have been updated)
      await fetchUsers();
      return result;
    } catch (err) {
      console.error('updateUserPasswords() failed:', err);
      throw err;
    }
  };

  // call the admin updateUsers cloud function via HTTPS using API key from app options
  const updateUsers = async (
    updates: Array<{ uid?: string; email?: string; fields: Record<string, any> }>
  ): Promise<any> => {
    const options: any = app?.options || {};
    const projectId = options.projectId as string | undefined;
    const apiKey = options.apiKey as string | undefined;
    if (!projectId || !apiKey) {
      throw new Error('client-config: missing projectId or apiKey');
    }

    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/updateUsers?key=${apiKey}`;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('unauthenticated: must be signed in to call updateUsers');
    }
    const idToken = await currentUser.getIdToken();

    const body = { data: { updates } };

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('updateUsers() remote error:', res.status, text);
        throw new Error(`remote-error: ${res.status}`);
      }

      const json = await res.json();
      const result = (json && (json.result ?? json)) as any;
      // refresh local users list after updates
      await fetchUsers();
      return result;
    } catch (err) {
      console.error('updateUsers() failed:', err);
      throw err;
    }
  };

  return (
    <UsersByManufacturerContext.Provider value={{ users, refresh: fetchUsers, createUser, deleteUsers, updateUserPasswords, updateUsers }}>
      {children}
    </UsersByManufacturerContext.Provider>
  );
};

export const useUsersByManufacturer = () => {
  const ctx = useContext(UsersByManufacturerContext);
  if (!ctx) throw new Error('useUsersByManufacturer must be used within UsersDataFetcher');
  return ctx;
};

export default UsersDataFetcher;
